import React, {
    PropsWithChildren,
    useCallback,
    useImperativeHandle,
    useMemo,
    useState,
} from "react";
import type { Ref } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { without } from "@common/helpers/collections";
import axios from "axios";
import { v7 as uuid } from "uuid";
import { FinancialDocument } from "@common/types/financialDocument";
import {
    DOCUMENT_TAB_UPLOAD_MIME_TYPES,
    MAX_ARCHIVE_FILE_S3_SIZE_MB,
    MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB,
    MAX_QBD_EXTRACTOR_JSON_FILE_SIZE_MB,
    QBD_EXTRACTOR_JSON_CONTENT_TYPE,
    isDirectS3UploadFile,
    maxUploadSizeBytesForFile,
} from "@common/constants/financial-documents";
import { useScope } from "@state/scopeContext.lib";
import { uploadDocument } from "@lib/financialDocument";
import { toBase64 } from "@helpers/file";
import {
    useCreateUploadUrlMutation,
    useProcessS3Upload,
} from "@api/financialDocument.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { UploadingDocumentPlaceholder } from "../lib";
import {
    FinancialDocumentUploadContextProvider,
    FinancialDocumentUploadContextValue,
} from "./financialDocumentUploadContext";

const DEFAULT_ACCEPT = [...DOCUMENT_TAB_UPLOAD_MIME_TYPES];

function invalidTypeMessage(acceptTypes: readonly string[]): string {
    const allowsJson = acceptTypes.includes(QBD_EXTRACTOR_JSON_CONTENT_TYPE);
    return allowsJson
        ? "Only PDF, PNG, JPEG, spreadsheet (CSV, XLS, XLSX, XLSM), Word (DOC, DOCX), ZIP, and JSON files are allowed"
        : "Only PDF, PNG, JPEG, spreadsheet (CSV, XLS, XLSX, XLSM), Word (DOC, DOCX), and ZIP files are allowed";
}

interface FinancialDocumentUploadProviderProps {
    accept?: string[];
    multiple?: boolean;
    onCompleted?(uploadedDocuments: FinancialDocument[]): Promise<void>;
}

export const FinancialDocumentUploadProvider = ({
    children,
    accept,
    multiple = true,
    onCompleted,
    ref,
}: PropsWithChildren<FinancialDocumentUploadProviderProps> & {
    ref?: Ref<FinancialDocumentUploadContextValue>;
}) => {
    const { workspaceId } = useScope();
    const createUploadUrlMutation = useCreateUploadUrlMutation();
    const processS3Upload = useProcessS3Upload(workspaceId ?? "");

    const [uploadingDocuments, setUploadingDocuments] = useState<
        UploadingDocumentPlaceholder[]
    >([]);

    const resolvedAccept = accept ?? DEFAULT_ACCEPT;
    const { error } = useToaster();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const handleFilesRejected = useCallback(
        (rejections: FileRejection[]) => {
            const errorMessages: Record<string, string> = {
                "file-too-large": `Max allowed size is ${MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB}MB`,
                "file-invalid-type": invalidTypeMessage(resolvedAccept),
            };
            rejections.forEach((rejection) => {
                error(
                    errorMessages[rejection.errors[0].code]
                        ? `${rejection.file.name} - ${
                              errorMessages[rejection.errors[0].code]
                          }`
                        : "Unknown error when uploading file",
                );
            });
        },
        [error, resolvedAccept],
    );

    const updateUpload = useCallback(
        (
            placeholder: UploadingDocumentPlaceholder,
            payload: Partial<UploadingDocumentPlaceholder>,
        ) => {
            setUploadingDocuments((prev) =>
                prev.map((el) =>
                    el.fileName === placeholder.fileName
                        ? {
                              ...el,
                              ...payload,
                          }
                        : el,
                ),
            );
        },
        [],
    );

    const regularUpload = useCallback(
        async (file: File) => {
            const placeholder: UploadingDocumentPlaceholder = {
                fileName: file.name,
                isUploading: true,
            };

            const contentToUpload = await toBase64(file);

            try {
                setUploadingDocuments((prev) => [...prev, placeholder]);
                const financialDocument = await uploadDocument({
                    workspaceId: workspaceId ?? "",
                    file: contentToUpload,
                    filename: file.name,
                    contentType: file.type,
                });

                updateUpload(placeholder, {
                    isUploading: false,
                    financialDocument,
                });

                return [financialDocument];
            } catch {
                setUploadingDocuments((prev) => without(prev, placeholder));
            }
        },
        [updateUpload, workspaceId],
    );

    const directS3Upload = useCallback(
        async (file: File) => {
            const placeholder: UploadingDocumentPlaceholder = {
                fileName: file.name,
                isUploading: true,
            };

            try {
                setUploadingDocuments((prev) => [...prev, placeholder]);
                const uploadPath = `${uuid()}/${file.name}`;

                const uploadUrl = await createUploadUrlMutation.mutateAsync({
                    path: uploadPath,
                });

                await axios.put(uploadUrl, file, {
                    headers: {
                        "Content-Type": file.type,
                    },
                    onUploadProgress: ({ total, loaded }) => {
                        if (total && loaded) {
                            updateUpload(placeholder, {
                                progress: Math.round((loaded / total) * 100),
                            });
                        }
                    },
                });

                const result = await processS3Upload.mutateAsync({
                    workspaceId: workspaceId ?? "",
                    path: uploadPath,
                });

                updateUpload(placeholder, {
                    isUploading: false,
                });

                return result;
            } catch {
                setUploadingDocuments((prev) => without(prev, placeholder));
            }
        },
        [createUploadUrlMutation, processS3Upload, updateUpload, workspaceId],
    );

    const processFile = useCallback(
        async (file: File) => {
            if (isDirectS3UploadFile(file)) {
                return await directS3Upload(file);
            } else {
                return await regularUpload(file);
            }
        },
        [directS3Upload, regularUpload],
    );

    const handleFilesAccepted = useCallback(
        async (files: File[]) => {
            if (refuseIfViewOnly()) {
                return;
            }

            if (
                files.some(
                    (file) => file.size > maxUploadSizeBytesForFile(file),
                )
            ) {
                error(
                    `Max allowed size is ${MAX_FINANCIAL_DOCUMENT_FILE_SIZE_MB}MB, ${MAX_ARCHIVE_FILE_S3_SIZE_MB}MB for archives, or ${MAX_QBD_EXTRACTOR_JSON_FILE_SIZE_MB}MB for QuickBooks extractor JSON`,
                );
                return;
            }

            const results = await Promise.allSettled(files.map(processFile));
            const successful = results
                .filter((result) => result.status === "fulfilled")
                .map((result) => result.value as FinancialDocument[])
                .flat();

            await onCompleted?.(successful);
        },
        [error, onCompleted, processFile, refuseIfViewOnly],
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDropRejected: handleFilesRejected,
        onDropAccepted: handleFilesAccepted,
        accept: resolvedAccept,
        multiple,
        noClick: true,
        noKeyboard: true,
    });

    const clearUploads = useCallback(() => {
        setUploadingDocuments([]);
    }, []);

    const handleOpen = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        open();
    }, [refuseIfViewOnly, open]);

    const value = useMemo(
        () => ({
            isDragActive,
            inputProps: getInputProps(),
            rootProps: getRootProps(),
            open: handleOpen,
            uploadingDocuments,
            updateUploads: setUploadingDocuments,
            clearUploads,
        }),
        [
            clearUploads,
            getInputProps,
            getRootProps,
            isDragActive,
            handleOpen,
            uploadingDocuments,
        ],
    );

    useImperativeHandle(ref, () => value, [value]);

    return (
        <FinancialDocumentUploadContextProvider value={value}>
            {children}
        </FinancialDocumentUploadContextProvider>
    );
};
